import React from "react";
import { useDroppable, DragOverlay } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { classNames } from "../../lib/Util";

import "./DraggableList.scss";

export type SortableDragHandleProps = React.HTMLAttributes<HTMLDivElement> & {
    disabled?: boolean;
};

type UseSortableDragHandleOptions = {
    id: string;
    disabled?: boolean;
    data?: Record<string, unknown>;
};

/**
 * Shared sortable state for handle-activated drag (listeners on handle only, ref on container).
 */
export const useSortableDragHandle = ({ id, disabled = false, data }: UseSortableDragHandleOptions) => {
    const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
        id,
        data,
        disabled,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandleProps: SortableDragHandleProps = disabled ? { disabled: true } : { ...attributes, ...listeners };

    return { setNodeRef, style, dragHandleProps, isDragging, disabled };
};

type DraggableListRowProps = {
    Icon?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    itemRef?: (element: HTMLDivElement | null) => void;
    dragHandleProps?: SortableDragHandleProps;
};

/**
 * Presentational list row (handle + content). Safe to render inside DragOverlay.
 */
export const DraggableListRow = ({
    Icon = <i className="fas fa-sort" />,
    children,
    className,
    style,
    itemRef,
    dragHandleProps,
}: DraggableListRowProps) => (
    <div ref={itemRef} style={style} className={classNames("draggable-list__item", className)}>
        <div className="draggable-list__item-drag-handle" {...dragHandleProps}>
            {Icon}
        </div>
        {children}
    </div>
);

type DragHandleProps = {
    dragHandleProps: SortableDragHandleProps;
    title?: string;
    className?: string;
};

/**
 * Drag handle for table cells or other non-list layouts. Spread dragHandleProps from useSortableDragHandle.
 */
export const DragHandle = ({ dragHandleProps, title, className }: DragHandleProps) => {
    const { disabled, style, ...restDragHandleProps } = dragHandleProps;

    return (
        <div
            {...restDragHandleProps}
            className={classNames("draggable-list__drag-handle", className, {
                "draggable-list__drag-handle--disabled": disabled,
            })}
            style={{
                width: "20px",
                textAlign: "center",
                cursor: disabled ? "not-allowed" : restDragHandleProps.onPointerDown ? "grab" : "default",
                userSelect: "none",
                ...style,
            }}
            title={title}
        >
            <i className={classNames("fas fa-sort", { "icon-interactive": !disabled })} />
        </div>
    );
};

type SortableTableBodyProps = {
    id: string;
    disabled?: boolean;
    data?: Record<string, unknown>;
    className?: string;
    children: (dragHandleProps: SortableDragHandleProps) => React.ReactNode;
};

/**
 * Sortable wrapper for one or more table rows inside a shared tbody (planning table orders).
 */
export const SortableTableBody = ({ id, disabled = false, data, className, children }: SortableTableBodyProps) => {
    const { setNodeRef, style, dragHandleProps, isDragging } = useSortableDragHandle({ id, disabled, data });

    return (
        <tbody
            ref={setNodeRef}
            style={style}
            className={classNames("sortable-table-body", className, {
                "draggable-sortable__drop-slot": isDragging,
            })}
        >
            {children(dragHandleProps)}
        </tbody>
    );
};

type SortableItemProps = {
    id: string;
    selected?: boolean;
    selectable?: boolean;
    type: string;
    isDisabled?: () => boolean;
    Icon?: React.ReactNode;
    children: React.ReactNode;
};

/**
 * A list item that can be reordered via drag-and-drop.
 * Renders a drag handle and uses useSortable hook.
 */
export const SortableItem = ({
    id,
    selected,
    selectable = false,
    type,
    isDisabled = () => false,
    Icon = <i className="fas fa-sort" />,
    children,
}: SortableItemProps) => {
    const disabled = isDisabled();
    const { setNodeRef, style, dragHandleProps, isDragging } = useSortableDragHandle({
        id,
        disabled,
        data: { type },
    });

    return (
        <DraggableListRow
            itemRef={setNodeRef}
            style={style}
            Icon={Icon}
            dragHandleProps={dragHandleProps}
            className={classNames(
                {
                    "draggable-list__item--locked": disabled,
                    "draggable-list__item--selected": selected,
                    "draggable-list__item--selectable": selectable,
                },
                { "draggable-sortable__drop-slot": isDragging }
            )}
        >
            {children}
        </DraggableListRow>
    );
};

type DroppableItemProps = {
    id: string;
    selected?: boolean;
    children: React.ReactNode;
    Icon?: React.ReactNode;
};

/**
 * A list item that acts as a drop target (e.g. for adding items from outside the list).
 * Highlights when a draggable is over it.
 */
export const DroppableItem = ({
    id,
    selected = false,
    children,
    Icon = <i className="fas fa-sort" />,
}: DroppableItemProps) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `droppable-${id}`,
        data: { id, droppable: true },
    });

    return (
        <DraggableListRow
            itemRef={setNodeRef}
            Icon={Icon}
            className={classNames("draggable-list__item--drop-target", {
                "draggable-list__item--selected": selected,
                "draggable-list__item--external-item-over": isOver,
            })}
        >
            {children}
        </DraggableListRow>
    );
};

type DragOverlayItemProps = {
    children: React.ReactNode;
};

/**
 * Overlay wrapper for the item being dragged.
 * Renders the dragged item with a drop animation via the DragOverlay component.
 */
export const DragOverlayItem = ({ children }: DragOverlayItemProps) => (
    <DragOverlay
        className="draggable-list__item--dragged"
        dropAnimation={{
            duration: 150,
            easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
    >
        {children}
    </DragOverlay>
);
